import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { message, Modal, Button } from "antd";
import {
  facebookService,
  PageInfo,
  InstagramAccount,
} from "src/services/FacebookAPIs";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnected: () => void;
}

interface Option {
  label: string;
  value: string;
}

const FacebookModal: React.FC<ModalProps> = ({ isOpen, onClose, onConnected }) => {
  const [accessToken, setAccessToken] = useState<string>("");
  const [selectedAccountId, setSelectedAccountId] = useState<Option | null>(null);
  const [adAccountOptions, setAdAccountOptions] = useState<Option[]>([]);
  const [, setAdAccounts] = useState<string[]>([]);
  const [pages, setPages] = useState<PageInfo[]>([]);
  const [selectedPage, setSelectedPage] = useState<Option | null>(null);
  const [instagramAccount, setInstagramAccount] = useState<InstagramAccount | null>(null);
  const [hasExistingConnection, setHasExistingConnection] = useState<boolean>(false);
  const [isFieldsLocked, setIsFieldsLocked] = useState<boolean>(false);
  const navigate = useNavigate();

  // ============ FETCH EXISTING CONNECTION ============
  useEffect(() => {
    const fetchExistingAccountDetails = async () => {
      try {
        const tenant_id = localStorage.getItem("tenant_id");
        const data = await facebookService.fetchExistingAccountDetails(tenant_id);
        if (data.status && data.facebook_access_token) {
          setHasExistingConnection(true);
          setIsFieldsLocked(true);
          setAccessToken(data.facebook_access_token);
          setSelectedAccountId({ label: data.facebook_ad_account_id, value: data.facebook_ad_account_id });
          setSelectedPage({ label: data.facebook_page_name, value: data.facebook_page_name });
          setInstagramAccount({ id: "existing", name: data.instagram_account_name });
        } else {
          setHasExistingConnection(false);
          setIsFieldsLocked(false);
        }
      } catch (error) {
        message.error("Failed to fetch existing account details");
      }
    };

    if (isOpen) {fetchExistingAccountDetails();}
  }, [isOpen]);

  // ============ LOGIN ============
  const login = () => {
    window.FB.login(
      (response: any) => {
        if (response.authResponse) {
          handleLogin(response);
        }
      },
      {
        scope:
          "email, public_profile, ads_read, ads_management, business_management, pages_show_list, pages_read_engagement, instagram_basic, instagram_manage_insights, instagram_content_publish, pages_messaging, page_events, leads_retrieval, pages_manage_engagement, pages_manage_posts, pages_read_user_content, read_insights",
      }
    );
  };

  const handleLogin = async (response: any) => {
    if (response.authResponse) {
      const token = response.authResponse.accessToken;
      setAccessToken(token);
      window.FB.api("/me", { fields: "name,email" }, async (userInfo: any) => {
        try {
          const data = await facebookService.saveTokenToServer(token, userInfo);
          const longToken = data.long_lived_token || token;
          setAccessToken(longToken);
          fetchAdAccounts(longToken);
        } catch {
          message.error("Error during Facebook login");
        }
      });
    }
  };

  // ============ FETCH ACCOUNTS ============
  const fetchAdAccounts = async (token: string) => {
    try {
      const data = await facebookService.fetchAdAccounts(token);
      if (data.ad_account_ids) {
        const formattedOptions = data.ad_account_ids.map((id: string) => ({
          label: id,
          value: id,
        }));
        setAdAccounts(data.ad_account_ids);
        setAdAccountOptions(formattedOptions);
      }
      if (data.pages) {setPages(data.pages);}
    } catch {
      message.error("Error fetching ad accounts");
    }
  };

  // ============ INSTAGRAM ============
  const fetchInstagramAccount = async (pageId: string, pageAccessToken: string) => {
    try {
      const data = await facebookService.fetchInstagramAccount(pageId, pageAccessToken);
      if (data.success && data.instagram_account) {setInstagramAccount(data.instagram_account);}
    } catch {
      message.error("Failed to fetch Instagram account info");
    }
  };

  const handlePageSelection = (selectedOption: Option | null) => {
    setSelectedPage(selectedOption);
    if (selectedOption) {
      const selectedPageInfo = pages.find(page => page.page_id === selectedOption.value);
      if (selectedPageInfo)
        {fetchInstagramAccount(selectedPageInfo.page_id, selectedPageInfo.page_access_token);}
    } else {
      setInstagramAccount(null);
    }
  };

  // ============ CONNECTION ACTIONS ============
  const testConnection = async () => {
    if (!accessToken || !selectedAccountId) {
      message.error("Please log in and select an Ad Account ID.");
      return;
    }
    try {
      const result = await facebookService.testConnection({
        accessToken,
        adAccountId: selectedAccountId.value,
      });
      result.success ? message.success(result.message) : message.error(result.message);
    } catch {
      message.error("Error testing connection");
    }
  };

  const createConnection = async () => {
    if (!accessToken || !selectedAccountId || !selectedPage || !instagramAccount) {
      message.error("Please fill in all fields before connecting.");
      return;
    }
    const selectedPageInfo = pages.find(page => page.page_id === selectedPage.value);
    if (!selectedPageInfo) {
      message.error("Selected page not found.");
      return;
    }

    try {
      const result = await facebookService.createConnection({
        accessToken,
        adAccountId: selectedAccountId.value,
        page_id: selectedPage.value,
        page_name: selectedPageInfo.page_name,
        page_access_token: selectedPageInfo.page_access_token,
        instagram_account_id: instagramAccount.id,
        instagram_account_name: instagramAccount.name,
        user_id: localStorage.getItem("user_id"),
        tenant_id: localStorage.getItem("tenant_id"),
        source: "facebook",
      });

      if (result.status) {
        message.success(result.message);
        onConnected();
        navigate("/welcome-to-datatram/connect-sources", { state: { from: "basic-information" } });
        onClose();
      } else {message.error(result.message || "Connection failed");}
    } catch {
      message.error("Error creating connection");
    }
  };

  const removeConnection = async () => {
    try {
      const tenant_id = localStorage.getItem("tenant_id");
      const result = await facebookService.removeConnection(tenant_id);
      if (result.status) {
        message.success("Connection removed successfully");
        setHasExistingConnection(false);
        setIsFieldsLocked(false);
        setAccessToken("");
        setSelectedAccountId(null);
        setSelectedPage(null);
        setInstagramAccount(null);
      } else {message.error("Failed to remove connection");}
    } catch {
      message.error("Error removing connection");
    }
  };

  // ============ RENDER ============
  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      width={600}
      className="facebook-modal"
    >
      <div className="innerbody">
        <img className="logo-fb mb-3" src="/images/logo-fb.svg" alt="Facebook Icon" />

        {/* Access Token */}
        <div className="form-selection access-tkn-fb">
          <label htmlFor="accessToken">Access Token</label>
          <input
            type="password"
            id="accessToken"
            className="form-control"
            value={accessToken}
            readOnly
          />
        </div>

        {!hasExistingConnection && (
          <Button
            type="primary"
            onClick={login}
            disabled={isFieldsLocked}
            style={{ width: "300px", height: "50px", marginTop: "20px" }}
          >
            Continue with Facebook
          </Button>
        )}

        {/* Select Account */}
        <div className="form-input mt-3">
          <label>Select Account ID</label>
          <Select
            options={adAccountOptions}
            value={selectedAccountId}
            onChange={setSelectedAccountId}
            placeholder="Select Account ID"
            isDisabled={isFieldsLocked}
          />
        </div>

        {/* Select Page */}
        <div className="form-input mt-3">
          <label>Select Facebook Page</label>
          <Select
            options={pages.map(p => ({ label: p.page_name, value: p.page_id }))}
            value={selectedPage}
            onChange={handlePageSelection}
            placeholder="Select Facebook Page"
            isDisabled={isFieldsLocked}
          />
        </div>

        {/* Instagram */}
        {instagramAccount && (
          <div className="form-input mt-3">
            <label>Instagram Account</label>
            <input type="text" className="form-control" value={instagramAccount.name} readOnly />
          </div>
        )}

        {/* Buttons */}
        <div className="button-group mt-4 d-flex justify-content-center flex-wrap gap-3">
          {hasExistingConnection ? (
            <>
              <Button className="button button--connection" onClick={removeConnection}>
                Remove Connection
              </Button>
              <Button className="button button--danger" onClick={onClose}>
                Cancel
              </Button>
              <Button
                className="button button--primary"
                onClick={() => {
                  setIsFieldsLocked(false);
                  setHasExistingConnection(false);
                }}
              >
                Change Account
              </Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={createConnection}>
                Create Connection
              </Button>
              <Button className="button button--danger" onClick={onClose}>
                Cancel
              </Button>
              <Button type="default" onClick={testConnection}>
                Test Connection
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default FacebookModal;
